import {useEffect, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {Alert, Select} from 'antd';
import {toast} from 'react-toastify';
import DBModal from 'components/modal/modal';
import {Button} from '@dabafinance/react-components';
import {ModalShell} from 'pages/adminOpportunities/stocksListings/ViewOrders/ui/ModalShell';
import {RESOLVE_TICKET} from 'graphql/mutations/aiHelpdesk';
import {
  GET_HELPDESK_CATEGORIES,
  GET_HELPDESK_FEATURES,
  GET_HELPDESK_FEATURE_ISSUES,
} from 'graphql/queries/flowBuilder';
import styles from './ResolveTicketModal.module.scss';

const ALL = {page: 1, limit: 100};

const unwrap = (node, type) => (node?.__typename === type ? node.data : null);

const options = (rows, label = 'name') =>
  (rows || []).map(row => ({value: row.id, label: row[label]}));

/**
 * Closing a ticket as an agent means saying what it was about: the backend
 * rejects a human resolution without an active issue that matches the
 * ticket's product and language.
 *
 * A ticket that already has an issue keeps it unless the agent picks another
 * (e.g. because the old one was deactivated). On any error the dialog stays
 * open with the message, and the ticket is left as it was.
 */
export const ResolveTicketModal = ({ticket, isOpen, onClose, onResolved}) => {
  const product = ticket?.product || 'MOBILE';
  const language = ticket?.language || 'EN';
  const currentIssueId = ticket?.featureIssueId || null;
  const currentIssueTitle = ticket?.featureIssue?.title || null;

  const [choosing, setChoosing] = useState(!currentIssueId);
  const [categoryId, setCategoryId] = useState(null);
  const [featureId, setFeatureId] = useState(null);
  const [issueId, setIssueId] = useState(null);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setChoosing(!currentIssueId);
    setCategoryId(null);
    setFeatureId(null);
    setIssueId(null);
    setProblem(null);
  }, [isOpen, currentIssueId]);

  const scope = {product, language, isActive: true, pagination: ALL};
  const categories = useQuery(GET_HELPDESK_CATEGORIES, {
    variables: {input: scope},
    skip: !isOpen || !choosing,
    errorPolicy: 'all',
  });
  const features = useQuery(GET_HELPDESK_FEATURES, {
    variables: {input: {...scope, categoryId}},
    skip: !isOpen || !categoryId,
    errorPolicy: 'all',
  });
  const issues = useQuery(GET_HELPDESK_FEATURE_ISSUES, {
    variables: {input: {...scope, featureId}},
    skip: !isOpen || !featureId,
    errorPolicy: 'all',
  });

  const [resolve, {loading}] = useMutation(RESOLVE_TICKET, {
    errorPolicy: 'all',
  });

  const featureIssueId = choosing ? issueId : currentIssueId;

  const submit = async () => {
    if (!featureIssueId) {
      setProblem('Pick the issue this ticket was about.');
      return;
    }
    setProblem(null);
    try {
      // CSAT is the customer's alone: always null from the admin side.
      const res = await resolve({
        variables: {
          input: {
            id: ticket.id,
            resolutionType: 'human',
            featureIssueId,
            csatScore: null,
            csatComment: null,
          },
        },
      });
      const node = res.data?.resolveTicket;
      if (node?.__typename === 'Error' || res.errors?.length) {
        setProblem(node?.message || res.errors[0].message);
        return;
      }
      toast.success('Ticket resolved');
      onResolved?.();
      onClose();
    } catch (error) {
      setProblem(error.message || "Couldn't resolve the ticket. Try again.");
    }
  };

  const categoryRows = unwrap(
    categories.data?.getHelpdeskCategories,
    'ResponseWithHelpdeskCategories',
  );
  const featureRows = unwrap(
    features.data?.getHelpdeskFeatures,
    'ResponseWithHelpdeskFeatures',
  );
  const issueRows = unwrap(
    issues.data?.getHelpdeskFeatureIssues,
    'ResponseWithHelpdeskFeatureIssues',
  );
  const languageName = language === 'FR' ? 'French' : 'English';

  return (
    <DBModal
      isOpen={isOpen}
      handleClose={onClose}
      destroyOnClose
      width={540}
      content={
        <ModalShell
          title="Resolve ticket"
          subtitle={`Choose what this ticket was about. Only active ${languageName} topics for this product are listed.`}
          footer={
            <>
              <Button onClick={onClose} type="secondary" label="Cancel" />
              <Button
                disabled={loading || !featureIssueId}
                onClick={submit}
                label={loading ? 'Resolving…' : 'Resolve'}
              />
            </>
          }>
          {currentIssueId && !choosing && (
            <div className={styles.current}>
              <span className={styles.currentLabel}>Issue</span>
              <strong>
                {currentIssueTitle || 'The ticket’s current issue'}
              </strong>
              <button
                type="button"
                className={styles.change}
                onClick={() => setChoosing(true)}>
                Choose another
              </button>
            </div>
          )}

          {choosing && (
            <div className={styles.fields}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Category</span>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Pick a category"
                  loading={categories.loading}
                  value={categoryId || undefined}
                  onChange={value => {
                    setCategoryId(value);
                    setFeatureId(null);
                    setIssueId(null);
                  }}
                  options={options(categoryRows)}
                />
              </label>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Feature</span>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={
                    categoryId ? 'Pick a feature' : 'Pick a category first'
                  }
                  disabled={!categoryId}
                  loading={features.loading}
                  value={featureId || undefined}
                  onChange={value => {
                    setFeatureId(value);
                    setIssueId(null);
                  }}
                  options={options(featureRows)}
                />
              </label>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Issue</span>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={
                    featureId ? 'Pick an issue' : 'Pick a feature first'
                  }
                  disabled={!featureId}
                  loading={issues.loading}
                  value={issueId || undefined}
                  onChange={setIssueId}
                  options={options(issueRows, 'title')}
                />
              </label>
              {currentIssueId && (
                <button
                  type="button"
                  className={styles.change}
                  onClick={() => setChoosing(false)}>
                  Keep the current issue
                  {currentIssueTitle ? `: ${currentIssueTitle}` : ''}
                </button>
              )}
            </div>
          )}

          {problem && (
            <Alert
              type="error"
              showIcon
              className={styles.problem}
              message={problem}
            />
          )}
        </ModalShell>
      }
    />
  );
};
